/**
 * Live preview engine: pooled <video> elements (one per ACTIVE CLIP, keyed by
 * mediaplan element keys `${sourceId}#${n}`) + WebAudio per-element gains,
 * rAF clock, shared compositor draw.
 *
 * Sync model (v1, simple + robust): the rAF clock is master; video elements
 * are nudged (currentTime reset) whenever they drift more than DRIFT_LIMIT
 * from the clip-mapped time. Elements are preloaded/seeked shortly before
 * their clip starts (against the `#0` element of each source); `#1+` clones
 * exist only while a source is demanded by several clips at once.
 */
import type { Archive } from '../api/archives';
import { totalDuration } from '../format/helpers';
import type { AudioClip, Edit, PandoraVideoSource, VideoClip } from '../format/types';
import { drawFrame, type Drawable } from './compositor';
import { getClipCache, sourceKeyOf, type ClipCache, type ClipCacheEntry } from './clipcache';
import {
  assignElements,
  clipElementKey,
  elementKey,
  parseElementKey,
  planMedia,
  type MediaDemand,
} from './mediaplan';
import {
  frameCacheKey,
  getFrameCache,
  pickFrameResolution,
  quantizeFrameTime,
  type FrameCache,
} from './framecache';
import { mediaParams, mediaUrl, sourceMediaUrl } from './mediaurls';
import { getPartOffsets } from './partoffsets';

const DRIFT_LIMIT = 0.08; // seconds
const PRELOAD_AHEAD = 3; // seconds before clip start to seek to in-point
const FALLBACK_GRID = 0.5; // seconds — quantization grid for fallback frame requests
const CLONE_TTL_MS = 5000; // drop #1+ clone elements unused for this long
const CACHED_TTL_MS = 60000; // cache-backed elements are cheap to rebuild but not free
// Element events after which readyState may have changed in either direction.
const READINESS_EVENTS = [
  'loadeddata',
  'canplay',
  'canplaythrough',
  'playing',
  'seeked',
  'waiting',
  'stalled',
  'emptied',
] as const;
const BUFFERING_SWEEP_MS = 250; // backstop re-check while paused and buffering
// Browsers clamp HTMLMediaElement.playbackRate roughly to this range and some
// throw outside it — clamp instead of throwing.
const MIN_MEDIA_RATE = 0.0625;
const MAX_MEDIA_RATE = 16;

export interface LoopRange {
  in: number;
  out: number;
}

/**
 * Playhead position for a wall-clock-mastered transport: time advances from
 * the anchor `tStart` at `rate` timeline-seconds per wall second. Re-anchoring
 * (on seek / setRate) means passing the current t/now as the new anchors.
 */
export function advancePlayhead(
  tStart: number,
  wallStartMs: number,
  nowMs: number,
  rate: number,
): number {
  return tStart + ((nowMs - wallStartMs) / 1000) * rate;
}

/** Wrap t into [loop.in, loop.out) once it has passed loop.out. */
export function wrapToLoop(t: number, loop: LoopRange | null): number {
  if (!loop || loop.out <= loop.in || t < loop.out) return t;
  return loop.in + ((t - loop.in) % (loop.out - loop.in));
}

function clampMediaRate(r: number): number {
  return Math.min(MAX_MEDIA_RATE, Math.max(MIN_MEDIA_RATE, r));
}

interface PoolEntry {
  el: HTMLVideoElement;
  url: string;
  srcNode: MediaElementAudioSourceNode | null;
  gain: GainNode | null;
  error: boolean;
  /** last time this element was demanded (clone TTL sweeping) */
  lastUsed: number;
  /**
   * Source-time corresponding to this element's t=0. Non-zero only for
   * cache-backed elements, whose file starts at the keyframe before clip.in.
   * ALL element-time access must go through elementTime()/mediaTimeOf().
   */
  startOffset: number;
  /** Cache entry this element plays from (holds a blob-URL reference). */
  cacheEntry: ClipCacheEntry | null;
}

/** Element time for a source-time position, honouring a cached file's offset. */
function elementTime(entry: PoolEntry, mediaTime: number): number {
  return mediaTime - entry.startOffset;
}

/** Set `preservesPitch` across the standard name and the legacy vendor ones. */
function setPreservesPitch(el: HTMLMediaElement, value: boolean): void {
  const target = el as HTMLMediaElement & {
    mozPreservesPitch?: boolean;
    webkitPreservesPitch?: boolean;
  };
  target.preservesPitch = value;
  target.mozPreservesPitch = value;
  target.webkitPreservesPitch = value;
}

/** Find a video/audio clip by id (overlay clips have no media). */
function findMediaClip(edit: Edit, clipId: string): VideoClip | AudioClip | null {
  for (const track of edit.tracks) {
    if (track.kind === 'overlay') continue;
    for (const clip of track.clips as (VideoClip | AudioClip)[]) {
      if (clip.id === clipId) return clip;
    }
  }
  return null;
}

interface UpcomingSeek {
  sourceId: string;
  mediaTime: number;
}

export class PreviewEngine {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private getEdit: () => Edit;
  private archives: Archive[];
  /** Optional preferred stream resolution (height px); defaults to max per archive. */
  private resolution?: number;

  /** element-key (`${sourceId}#${n}`) → pooled <video> */
  private pool = new Map<string, PoolEntry>();
  private images = new Map<string, HTMLImageElement>();
  /** clipId → element key, kept stable across frames by assignElements */
  private lastAssignment = new Map<string, string>();
  /** demands from the most recent refreshAssignment (fallback + buffering) */
  private lastDemands: MediaDemand[] = [];

  private audioCtx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private streamDest: MediaStreamAudioDestinationNode | null = null;

  private rafId = 0;
  private playing = false;
  private t = 0;
  private wallStart = 0;
  private tStart = 0;
  private rate = 1; // shuttle rate; <0 = reverse (fallback-frame path, see setRate)

  // frame-fallback state (shown while the live <video> isn't decode-ready)
  private frameCache: FrameCache = getFrameCache();
  private fallbacks = new Map<string, ImageBitmap>(); // clipId|sourceId → last-shown frame
  private inflightKey = new Map<string, string>(); // clipId|sourceId → frame key being fetched
  private partOffsets = new Map<string, number[]>(); // "site/item" → concat offsets
  private partOffsetsLoading = new Set<string>();
  private buffering = new Set<string>(); // sources currently on the fallback path
  private bufferingSweepId = 0; // safety re-check while anything is buffering
  private disposed = false;

  private clipCache: ClipCache = getClipCache();
  /** clipId → cache entry, recomputed per assignment (sync, no awaits). */
  private cachedForClip = new Map<string, ClipCacheEntry>();
  /** Blob-URL references held for currently-demanded cached clips. */
  private heldRefs = new Map<string, ClipCacheEntry>();


  onTime: ((t: number) => void) | null = null;
  onEnded: (() => void) | null = null;
  /** Fired when the set of buffering (fallback-frame) sources changes. */
  onBuffering: ((sourceIds: string[]) => void) | null = null;
  /**
   * Loop-region provider (wired like onTime by the engine's owner). While
   * playing forward, passing loop.out wraps the playhead back to loop.in.
   */
  getLoop: (() => LoopRange | null) | null = null;

  constructor(
    canvas: HTMLCanvasElement,
    getEdit: () => Edit,
    archives: Archive[],
    opts?: { resolution?: number },
  ) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
    this.getEdit = getEdit;
    this.archives = archives;
    this.resolution = opts?.resolution;
    this.syncPool();
    this.drawCurrent();
  }

  /**
   * Stream URL for a source. When a resolution preference is set (public
   * viewer), pick the archive resolution closest to it; otherwise defer to
   * `sourceMediaUrl` (max resolution — editor default, unchanged).
   */
  private sourceUrl(src: PandoraVideoSource): string | null {
    if (this.resolution === undefined) return sourceMediaUrl(src, this.archives);
    const archive = this.archives.find((a) => a.key === src.archive);
    if (!archive) return null;
    const res = pickFrameResolution(archive.resolutions, this.resolution);
    return mediaUrl(archive, src.item, src.part, { resolution: res });
  }

  /** Cache identity for a source — must match what sourceUrl() would fetch. */
  private sourceCacheKey(src: PandoraVideoSource): string | null {
    const archive = this.archives.find((a) => a.key === src.archive);
    if (!archive) return null;
    const res =
      this.resolution === undefined
        ? undefined
        : pickFrameResolution(archive.resolutions, this.resolution);
    const p = mediaParams(archive, { resolution: res });
    return sourceKeyOf(src.archive, src.item, src.part, p.resolution, p.format);
  }

  /**
   * The dedicated element key for a clip that is cached AND whose blob URL is
   * already materialized.
   *
   * The materialization check is essential: assignment runs every frame and
   * cannot await, so promising a cache element we can't yet construct would
   * leave the clip with no element at all. Until the blob resolves the clip
   * streams as before, and the next frame picks up the cache.
   */
  private cachedKeyFor = (clipId: string): string | undefined => {
    const entry = this.cachedForClip.get(clipId);
    if (!entry) return undefined;
    return this.clipCache.peekUrl(entry) ? clipElementKey(clipId) : undefined;
  };

  /**
   * Refresh clipId → cache-entry for the clips demanded at t (sync lookups).
   *
   * Reference ownership is deliberately two-party: this method holds one
   * reference per DEMANDED cached clip (so the blob stays materialized while
   * the playhead is on it), and each cache-backed element holds its own for its
   * lifetime (so a longer-lived element can't have its src revoked underneath
   * it). Both are released independently.
   */
  private refreshCacheLookups(demands: MediaDemand[]): void {
    const edit = this.getEdit();
    const next = new Map<string, ClipCacheEntry>();
    for (const d of demands) {
      const src = edit.sources[d.sourceId];
      if (!src || src.kind !== 'pandora-video') continue;
      const sk = this.sourceCacheKey(src);
      if (!sk) continue;
      const clip = findMediaClip(edit, d.clipId);
      if (!clip) continue;
      const entry = this.clipCache.lookup(sk, clip.in, clip.out);
      if (entry) next.set(d.clipId, entry);
    }
    this.cachedForClip = next;

    // Take a reference for newly-demanded entries, materializing if needed.
    for (const entry of next.values()) {
      if (this.heldRefs.has(entry.url)) continue;
      this.heldRefs.set(entry.url, entry);
      void this.clipCache.acquireUrl(entry).then((url) => {
        // Repaint when the blob lands so the switch to the cached element is
        // visible immediately while paused.
        if (url && !this.disposed && !this.playing) this.drawCurrent();
      });
    }
    // Drop references for entries no longer demanded. Any element still using
    // one keeps it alive through its own reference.
    const demanded = new Set([...next.values()].map((e) => e.url));
    for (const [url, entry] of this.heldRefs) {
      if (demanded.has(url)) continue;
      this.heldRefs.delete(url);
      this.clipCache.releaseUrl(entry);
    }
  }

  get currentTime(): number {
    return this.t;
  }
  get isPlaying(): boolean {
    return this.playing;
  }

  /** Call after the edit changed (sources may have been added/removed). */
  editChanged(): void {
    this.syncPool();
    this.sweepClones(performance.now());
    if (!this.playing) this.drawCurrent();
  }

  private syncPool(): void {
    const edit = this.getEdit();
    const wanted = new Set<string>();
    for (const [id, src] of Object.entries(edit.sources)) {
      if (src.kind === 'pandora-video') {
        wanted.add(id);
        // the per-source base element (`#0`) always exists so preloading works
        if (!this.pool.has(elementKey(id, 0))) this.createEntry(elementKey(id, 0), src);
        // multi-part items need concat offsets to address fallback frames
        if (src.part > 1) {
          const archive = this.archives.find((a) => a.key === src.archive);
          if (archive) this.loadPartOffsets(archive.site, src.item);
        }
      } else {
        // image source
        if (!this.images.has(id)) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = src.url;
          this.images.set(id, img);
        }
      }
    }
    // Cache-backed elements are keyed by CLIP, not source — they must survive
    // this sweep, which only knows about source ids. They are reclaimed by the
    // TTL sweep in sweepClones/syncMedia instead.
    const liveClips = new Set<string>();
    for (const track of edit.tracks) {
      if (track.kind === 'overlay') continue;
      for (const clip of track.clips) liveClips.add(clip.id);
    }
    for (const [key, entry] of this.pool) {
      const parsed = parseElementKey(key);
      const stale =
        parsed.kind === 'clip'
          ? !liveClips.has(parsed.clipId) // clip deleted from the edit
          : !wanted.has(parsed.sourceId);
      if (stale) this.disposeEntry(key, entry);
    }
  }

  private createEntry(
    key: string,
    src: PandoraVideoSource,
    cached?: { url: string; entry: ClipCacheEntry },
  ): PoolEntry | null {
    const url = cached?.url ?? this.sourceUrl(src);
    if (!url) return null;
    const el = document.createElement('video');
    el.crossOrigin = 'anonymous';
    el.preload = 'auto';
    el.playsInline = true;
    el.muted = true; // unmuted once routed through WebAudio
    // Varispeed, not time-stretch: browsers default playbackRate to
    // pitch-PRESERVING, but the exporter's resampler can only varispeed. Left
    // at the default, a sped-up clip would sound different in the export than
    // in here — so preview gives up pitch preservation to stay honest.
    // See mixAudio() in exporter.ts.
    setPreservesPitch(el, false);
    el.src = url;
    const entry: PoolEntry = {
      el,
      url,
      srcNode: null,
      gain: null,
      error: false,
      lastUsed: performance.now(),
      startOffset: cached?.entry.start ?? 0,
      cacheEntry: cached?.entry ?? null,
    };
    el.addEventListener('error', () => {
      entry.error = true;
      this.readinessChanged();
    });
    // Readiness is otherwise only sampled at the end of drawCurrent(), which
    // does not run while paused and idle — so a video that finishes buffering
    // after a scrub would leave `buffering` latched on forever. Push instead.
    for (const ev of READINESS_EVENTS) el.addEventListener(ev, this.readinessChanged);
    this.pool.set(key, entry);
    if (this.audioCtx) this.routeAudio(entry);
    return entry;
  }

  /** Pool entry for an element key, lazily cloning `#1+` elements on demand. */
  private ensureEntry(key: string, edit: Edit): PoolEntry | null {
    const existing = this.pool.get(key);
    if (existing) return existing;
    const parsed = parseElementKey(key);
    if (parsed.kind === 'clip') {
      const clip = findMediaClip(edit, parsed.clipId);
      if (!clip) return null;
      const src = edit.sources[clip.source];
      if (!src || src.kind !== 'pandora-video') return null;
      const cacheEntry = this.cachedForClip.get(parsed.clipId);
      if (!cacheEntry) return null;
      // retain(), not peekUrl(): the element must own a reference for its whole
      // life, or the URL can be revoked while it is still the element's src.
      const url = this.clipCache.retain(cacheEntry);
      if (!url) return null;
      const created = this.createEntry(key, src, { url, entry: cacheEntry });
      if (!created) this.clipCache.releaseUrl(cacheEntry); // balance on failure
      return created;
    }
    const src = edit.sources[parsed.sourceId];
    if (!src || src.kind !== 'pandora-video') return null;
    return this.createEntry(key, src);
  }

  private disposeEntry(key: string, entry: PoolEntry): void {
    for (const ev of READINESS_EVENTS) entry.el.removeEventListener(ev, this.readinessChanged);
    entry.el.pause();
    entry.el.removeAttribute('src');
    entry.el.load();
    entry.srcNode?.disconnect();
    entry.gain?.disconnect();
    // Hand back the blob-URL reference, or the Blob stays pinned for the
    // document's lifetime — this is the only teardown path for a pool entry.
    if (entry.cacheEntry) this.clipCache.releaseUrl(entry.cacheEntry);
    this.pool.delete(key);
  }

  /**
   * Drop `#1+` clones and cache-backed elements that no assigned clip has used
   * for a while. Cache elements get a longer TTL: re-creating one is cheap
   * (local blob) but costs a fresh MediaElementAudioSourceNode, so churning
   * them is not free.
   */
  private sweepClones(now: number): void {
    const assigned = new Set(this.lastAssignment.values());
    for (const [key, entry] of this.pool) {
      const parsed = parseElementKey(key);
      const ttl = parsed.kind === 'clip' ? CACHED_TTL_MS : CLONE_TTL_MS;
      if (parsed.kind === 'source' && parsed.n === 0) continue; // base element is permanent
      if (assigned.has(key)) continue;
      if (now - entry.lastUsed > ttl) this.disposeEntry(key, entry);
    }
  }

  private ensureAudio(): void {
    if (this.audioCtx) return;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.audioCtx = new Ctor();
    this.masterGain = this.audioCtx.createGain();
    this.masterGain.connect(this.audioCtx.destination);
    for (const entry of this.pool.values()) this.routeAudio(entry);
  }

  private routeAudio(entry: PoolEntry): void {
    if (!this.audioCtx || !this.masterGain || entry.srcNode) return;
    try {
      entry.srcNode = this.audioCtx.createMediaElementSource(entry.el);
      entry.gain = this.audioCtx.createGain();
      entry.gain.gain.value = 0;
      entry.srcNode.connect(entry.gain);
      entry.gain.connect(this.masterGain);
      entry.el.muted = false;
    } catch {
      // element may already be attached to a dead context — leave muted
      entry.el.muted = true;
    }
  }

  /** Audio stream tap for the MediaRecorder export fallback. */
  getAudioStream(): MediaStream {
    this.ensureAudio();
    if (!this.streamDest && this.audioCtx && this.masterGain) {
      this.streamDest = this.audioCtx.createMediaStreamDestination();
      this.masterGain.connect(this.streamDest);
    }
    return this.streamDest?.stream ?? new MediaStream();
  }

  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  play(): void {
    if (this.playing) return;
    this.ensureAudio();
    void this.audioCtx?.resume();
    const dur = totalDuration(this.getEdit());
    if (this.rate > 0 && this.t >= dur - 0.01) this.t = 0;
    this.playing = true;
    this.wallStart = performance.now();
    this.tStart = this.t;
    this.loop();
  }

  pause(): void {
    this.playing = false;
    this.rate = 1; // shuttle rate always resets on pause
    cancelAnimationFrame(this.rafId);
    for (const entry of this.pool.values()) entry.el.pause();
    this.drawCurrent();
  }

  /**
   * Shuttle rate (J/K/L). Re-anchors the wall clock at the current playhead so
   * rate changes take effect immediately. Forward rates scale each pooled
   * <video>'s playbackRate (clamped to the browser-supported range).
   *
   * Limitation: HTML video cannot decode backwards, so for rate < 0 all pool
   * elements are paused (audio gains go to 0 in syncMedia) and frames come
   * from the cached-archive-frame fallback path — reverse playback shows
   * quantized preview frames, silently.
   */
  setRate(rate: number): void {
    this.wallStart = performance.now();
    this.tStart = this.t;
    this.rate = rate;
    if (rate < 0) for (const entry of this.pool.values()) entry.el.pause();
  }

  seek(t: number): void {
    const dur = totalDuration(this.getEdit());
    this.t = Math.max(0, Math.min(t, dur));
    if (this.playing) {
      this.wallStart = performance.now();
      this.tStart = this.t;
    }
    this.hardSeekMedia(this.t);
    if (!this.playing) this.drawCurrent();
    this.onTime?.(this.t);
  }

  /** Hard-seek all media elements that would be active at t. */
  private hardSeekMedia(t: number): void {
    const demands = this.refreshAssignment(t);
    for (const d of demands) {
      const key = this.lastAssignment.get(d.clipId);
      const entry = key ? this.pool.get(key) : undefined;
      if (entry && isFinite(d.mediaTime)) {
        try {
          entry.el.currentTime = elementTime(entry, d.mediaTime);
        } catch {
          /* not loaded yet */
        }
      }
    }
  }

  dispose(): void {
    this.disposed = true;
    this.playing = false;
    cancelAnimationFrame(this.rafId);
    if (this.bufferingSweepId) {
      clearInterval(this.bufferingSweepId);
      this.bufferingSweepId = 0;
    }
    for (const entry of this.pool.values()) {
      for (const ev of READINESS_EVENTS) entry.el.removeEventListener(ev, this.readinessChanged);
      entry.el.pause();
      entry.el.removeAttribute('src');
      entry.el.load();
      // This duplicates disposeEntry's teardown, so it must duplicate the blob
      // release too — otherwise every cached element leaks its mp4 on unmount.
      if (entry.cacheEntry) this.clipCache.releaseUrl(entry.cacheEntry);
    }
    this.pool.clear();
    this.images.clear();
    this.lastAssignment.clear();
    this.cachedForClip.clear();
    // Engine-held references are separate from the per-element ones released above.
    for (const entry of this.heldRefs.values()) this.clipCache.releaseUrl(entry);
    this.heldRefs.clear();
    this.lastDemands = [];
    this.fallbacks.clear();
    this.inflightKey.clear();
    if (this.buffering.size) {
      this.buffering = new Set();
      this.onBuffering?.([]);
    }
    void this.audioCtx?.close();
    this.audioCtx = null;
  }

  // -- internals ------------------------------------------------------------

  private loop = (): void => {
    if (!this.playing) return;
    const now = performance.now();
    this.t = advancePlayhead(this.tStart, this.wallStart, now, this.rate);
    const dur = totalDuration(this.getEdit());
    if (this.rate > 0) {
      const loopRange = this.getLoop?.() ?? null;
      const wrapped = wrapToLoop(this.t, loopRange);
      if (wrapped !== this.t) {
        this.t = wrapped;
        this.tStart = wrapped;
        this.wallStart = now;
        this.hardSeekMedia(wrapped);
      }
    }
    if (this.rate > 0 ? this.t >= dur : this.t <= 0) {
      // ran off either end — stop (onEnded lets the UI reset its play state)
      this.t = this.rate > 0 ? dur : 0;
      this.playing = false;
      this.rate = 1;
      for (const entry of this.pool.values()) entry.el.pause();
      this.drawCurrent();
      this.onTime?.(this.t);
      this.onEnded?.();
      return;
    }
    this.syncMedia();
    this.drawCurrent();
    this.onTime?.(this.t);
    this.rafId = requestAnimationFrame(this.loop);
  };

  /**
   * Recompute the per-clip media plan at t and the (stable) clip→element
   * assignment, lazily creating pool entries for demanded element keys and
   * stamping their lastUsed. Called every synced frame, on hard seeks, and
   * before paused draws so the provider always sees a fresh assignment.
   */
  private refreshAssignment(t: number): MediaDemand[] {
    const edit = this.getEdit();
    const demands = planMedia(edit, t);
    // Must precede assignElements: it decides which clips get a cache element.
    this.refreshCacheLookups(demands);
    this.lastAssignment = assignElements(demands, this.lastAssignment, this.cachedKeyFor);
    this.lastDemands = demands;
    const now = performance.now();
    for (const d of demands) {
      const key = this.lastAssignment.get(d.clipId);
      if (!key) continue;
      const entry = this.ensureEntry(key, edit);
      if (entry) entry.lastUsed = now;
    }
    return demands;
  }

  /** Upcoming clips (within PRELOAD_AHEAD): seek their `#0` element to the in-point. */
  private collectUpcoming(t: number): UpcomingSeek[] {
    const edit = this.getEdit();
    const out: UpcomingSeek[] = [];
    for (const track of edit.tracks) {
      if (track.kind === 'overlay') continue;
      for (const clip of track.clips as (VideoClip | AudioClip)[]) {
        if (clip.start > t && clip.start - t < PRELOAD_AHEAD) {
          out.push({ sourceId: clip.source, mediaTime: clip.in });
        }
      }
    }
    return out;
  }

  private syncMedia(): void {
    const demands = this.refreshAssignment(this.t);
    const upcoming = this.collectUpcoming(this.t);
    // each element carries exactly ONE clip's time/rate/gain (no first-clip-
    // wins, no gain summing — a source demanded twice gets two elements)
    const demandByKey = new Map<string, MediaDemand>();
    for (const d of demands) {
      const key = this.lastAssignment.get(d.clipId);
      if (key) demandByKey.set(key, d);
    }
    const now = performance.now();

    for (const [key, entry] of this.pool) {
      const m = demandByKey.get(key);
      if (m && this.rate < 0) {
        // reverse shuttle: <video> can't decode backwards — keep the element
        // paused and silent; frames come from the fallback path (see setRate)
        if (!entry.el.paused) entry.el.pause();
        if (entry.gain && this.audioCtx)
          entry.gain.gain.setTargetAtTime(0, this.audioCtx.currentTime, 0.02);
      } else if (m) {
        const el = entry.el;
        const rate = clampMediaRate(m.rate * this.rate);
        if (el.playbackRate !== rate) el.playbackRate = rate;
        // BOTH sides of this comparison must be in element time. Comparing a
        // cached element's currentTime against a SOURCE time would read a drift
        // of ~clip.in every frame and hard-seek forever — a permanent stutter.
        const want = elementTime(entry, m.mediaTime);
        if (el.readyState >= 2 && Math.abs(el.currentTime - want) > DRIFT_LIMIT) {
          try {
            el.currentTime = want;
          } catch {
            /* ignore */
          }
        }
        if (el.paused && !entry.error) void el.play().catch(() => undefined);
        if (entry.gain && this.audioCtx) {
          entry.gain.gain.setTargetAtTime(m.gain, this.audioCtx.currentTime, 0.02);
        }
      } else {
        if (!entry.el.paused) entry.el.pause();
        if (entry.gain && this.audioCtx)
          entry.gain.gain.setTargetAtTime(0, this.audioCtx.currentTime, 0.02);
        const parsed = parseElementKey(key);
        if (parsed.kind === 'clip') {
          // Cache-backed: never preloaded (its whole file is already local) and
          // held far longer than a clone.
          if (now - entry.lastUsed > CACHED_TTL_MS) this.disposeEntry(key, entry);
        } else if (parsed.n === 0) {
          // preload runs against the per-source base element only
          const up = upcoming.find((u) => u.sourceId === parsed.sourceId);
          if (up && entry.el.readyState >= 1) {
            if (Math.abs(entry.el.currentTime - up.mediaTime) > 0.25) {
              try {
                entry.el.currentTime = up.mediaTime;
              } catch {
                /* ignore */
              }
            }
          }
        } else if (now - entry.lastUsed > CLONE_TTL_MS) {
          this.disposeEntry(key, entry);
        }
      }
    }
  }

  private provider = (sourceId: string, clipId?: string): Drawable | null => {
    // per-clip element via the current assignment; `#0` covers providers/
    // callers that don't pass a clip id (and clips not currently assigned)
    const assignedKey = clipId ? this.lastAssignment.get(clipId) : undefined;
    const entry =
      (assignedKey ? this.pool.get(assignedKey) : undefined) ??
      this.pool.get(elementKey(sourceId, 0));
    if (entry) {
      if (!entry.error && entry.el.readyState >= 2 && this.rate >= 0) return entry.el;
      // live video not decode-ready (or errored), or shuttling in reverse
      // (video can't decode backwards) → pandora frame fallback instead of a
      // black/stuck frame.
      return this.fallbackFrame(sourceId, clipId);
    }
    const img = this.images.get(sourceId);
    if (img && img.complete && img.naturalWidth > 0) return img;
    return null;
  };

  /**
   * A frame bitmap for a clip whose <video> isn't ready. Serves an instant
   * in-memory hit when available, otherwise kicks a (deduped) fetch and returns
   * the last-shown frame for the clip (better a slightly stale frame than
   * black). `null` only until the very first frame arrives. Keyed per clip
   * when a clip id is given — two clips of one source can need two frames.
   */
  private fallbackFrame(sourceId: string, clipId?: string): Drawable | null {
    const fbKey = clipId ?? sourceId;
    const req = this.frameRequest(sourceId, clipId);
    if (!req) return this.fallbacks.get(fbKey) ?? null;
    const { archive, item, t } = req;
    const res = pickFrameResolution(archive.resolutions);
    const opts = { resolution: res, grid: FALLBACK_GRID };

    const hit = this.frameCache.peek(archive, item, t, opts);
    if (hit) {
      this.fallbacks.set(fbKey, hit);
      return hit;
    }

    const key = frameCacheKey(archive.key, item, res, quantizeFrameTime(t, FALLBACK_GRID));
    if (this.inflightKey.get(fbKey) !== key) {
      this.inflightKey.set(fbKey, key);
      void this.frameCache.getFrame(archive, item, t, opts).then((bmp) => {
        const current = this.inflightKey.get(fbKey) === key;
        if (current) this.inflightKey.delete(fbKey);
        // A superseded fetch (scrubbed past while it was in flight) must not
        // clobber a newer frame — but it is still better than black if we have
        // nothing to show for this clip yet.
        if (bmp && (current || !this.fallbacks.has(fbKey))) {
          this.fallbacks.set(fbKey, bmp);
          if (!this.playing) this.drawCurrent();
        }
      });
    }
    return this.fallbacks.get(fbKey) ?? null;
  }

  /** Archive/item/concat-time to request a fallback frame for a clip at `this.t`. */
  private frameRequest(
    sourceId: string,
    clipId?: string,
  ): { archive: Archive; item: string; t: number } | null {
    const edit = this.getEdit();
    const src = edit.sources[sourceId];
    if (!src || src.kind !== 'pandora-video') return null;
    const archive = this.archives.find((a) => a.key === src.archive);
    if (!archive) return null;
    const media = this.lastDemands.find((d) =>
      clipId ? d.clipId === clipId : d.sourceId === sourceId,
    );
    if (!media || !isFinite(media.mediaTime)) return null;
    let offset = 0;
    if (src.part > 1) {
      const offsets = this.partOffsets.get(`${archive.site}/${src.item}`);
      if (offsets) offset = offsets[src.part - 1] ?? 0;
      else this.loadPartOffsets(archive.site, src.item); // approximate with 0 until loaded
    }
    return { archive, item: src.item, t: offset + media.mediaTime };
  }

  private loadPartOffsets(site: string, item: string): void {
    const key = `${site}/${item}`;
    if (this.partOffsets.has(key) || this.partOffsetsLoading.has(key)) return;
    this.partOffsetsLoading.add(key);
    void getPartOffsets(site, item).then(
      (offsets) => {
        this.partOffsets.set(key, offsets);
        this.partOffsetsLoading.delete(key);
        if (!this.playing) this.drawCurrent();
      },
      () => {
        this.partOffsetsLoading.delete(key);
      },
    );
  }

  /**
   * A pooled element's readiness changed. While paused nothing else redraws, so
   * repaint here too — otherwise the canvas keeps showing the fallback frame
   * even once the live video is decode-ready.
   */
  private readinessChanged = (): void => {
    if (this.disposed) return;
    if (this.playing) this.updateBuffering();
    else this.drawCurrent();
  };

  /**
   * While anything is buffering, re-check periodically. Events cover the normal
   * paths; this is the backstop for readiness transitions that arrive without
   * one (or that we miss during element churn), so the indicator can never
   * latch on permanently. Stops itself as soon as nothing is buffering.
   */
  private ensureBufferingSweep(): void {
    if (this.bufferingSweepId || this.disposed) return;
    this.bufferingSweepId = window.setInterval(() => {
      if (this.disposed || this.playing || this.buffering.size === 0) {
        clearInterval(this.bufferingSweepId);
        this.bufferingSweepId = 0;
        return;
      }
      this.drawCurrent();
    }, BUFFERING_SWEEP_MS);
  }

  /** Recompute which active sources are on the fallback path; notify on change. */
  private updateBuffering(): void {
    const next = new Set<string>();
    for (const d of this.lastDemands) {
      const key = this.lastAssignment.get(d.clipId);
      const entry = key ? this.pool.get(key) : undefined;
      if (entry && (entry.error || entry.el.readyState < 2)) next.add(d.sourceId);
    }
    let changed = next.size !== this.buffering.size;
    if (!changed) for (const id of next) if (!this.buffering.has(id)) { changed = true; break; }
    if (changed) {
      this.buffering = next;
      this.onBuffering?.([...next]);
    }
    if (next.size && !this.playing) this.ensureBufferingSweep();
  }

  private drawCurrent(): void {
    // paused draws (seek/invalidate/editChanged) don't run syncMedia — refresh
    // the assignment here so the provider maps clips to current elements
    if (!this.playing) this.refreshAssignment(this.t);
    const edit = this.getEdit();
    if (this.canvas.width !== edit.settings.width) this.canvas.width = edit.settings.width;
    if (this.canvas.height !== edit.settings.height)
      this.canvas.height = edit.settings.height;
    drawFrame(this.ctx, edit, this.t, this.provider);
    this.updateBuffering();
  }

  /** Redraw without changing time (e.g. after an edit while paused). */
  invalidate(): void {
    if (!this.playing) this.drawCurrent();
  }
}
